import React from 'react';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import LiveTv from '@mui/icons-material/LiveTv';
import RecordVoiceOver from '@mui/icons-material/RecordVoiceOver';
import MenuBook from '@mui/icons-material/MenuBook';
import Hub from '@mui/icons-material/Hub';
import { useTheme } from '@material-ui/core/styles';
import { readableHeading } from '../readablePalette';
import PriceVatDisclaimer from 'common/PriceVatDisclaimer';


const rows = [
  {
    servicio: 'Pauta Radio Visual',
    precioUsd: 95,
    detalle: [
      '12 spots diarios (aprox. 360 al mes).',
      'Mensajes institucionales: importancia de estar agremiado y servicios de salud y previsión social.',
    ],
    beneficio: 'Frecuencia y recordación gremial en señal.',
    Icon: LiveTv,
  },
  {
    servicio: 'Segmento «Voz gremial»',
    precioUsd: 85,
    detalle: [
      'Un espacio mensual dentro de Legalmente en Familia.',
      'Invitados de la directiva del Colegio para comentar reformas legales o hitos del gremio.',
    ],
    beneficio: 'Credibilidad junto a la Dra. Davogustto y la audiencia familiar.',
    Icon: RecordVoiceOver,
  },
  {
    servicio: 'Dominio SEO (TNO Digest)',
    precioUsd: 65,
    detalle: [
      '2 artículos semanales (8 al mes) en el portal TNO Digest.',
      'Temas orientados: rol del abogado en la protección familiar, avances del Colegio, ética jurídica en el siglo XXI.',
    ],
    beneficio: 'Autoridad en buscadores y referencia para la ciudadanía.',
    Icon: MenuBook,
  },
  {
    servicio: 'Autoridad digital',
    precioUsd: 45,
    detalle: [
      'Presencia fija en el portal web TNO.',
      'Menciones en redes vinculadas a la formación académica del Colegio.',
    ],
    beneficio: 'Canal permanente para formación y servicios al agremiado.',
    Icon: Hub,
  },
];

const TOTAL_USD = rows.reduce((s, r) => s + r.precioUsd, 0);

const PlanInstitutional = () => {
  const theme = useTheme();
  const h = readableHeading(theme);
  const deep = theme.palette.primary.main;
  const gold = theme.palette.secondary.main;

  return (
    <Box component="section" sx={{ my: { xs: 5, md: 6 } }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { sm: 'center' },
          gap: 2,
          mb: 3,
        }}
      >
        <Box
          sx={{
            width: 4,
            minHeight: { xs: 48, sm: 56 },
            borderRadius: 2,
            background: `linear-gradient(180deg, ${gold} 0%, ${deep} 100%)`,
            flexShrink: 0,
          }}
        />
        <Box>
          <Typography
            variant="overline"
            sx={{ color: gold, fontWeight: 700, letterSpacing: '0.16em', display: 'block', mb: 0.5 }}
          >
            Estructura de la alianza
          </Typography>
          <Typography
            variant="h4"
            component="h2"
            sx={{ fontWeight: 700, color: h, fontFamily: 'Georgia, "Times New Roman", serif', lineHeight: 1.2 }}
          >
            Plan institucional
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              mt: 1.25,
              fontWeight: 800,
              color: deep,
              letterSpacing: '0.02em',
              fontFeatureSettings: '"tnum"',
            }}
          >
            {`${rows.map((r) => `$${r.precioUsd}`).join(' + ')} = $${TOTAL_USD} `}
            <Box component="span" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.95em' }}>
              USD/mes
            </Box>
          </Typography>
          <Typography variant="body1" color="textSecondary" sx={{ mt: 1.5, maxWidth: 720, lineHeight: 1.75 }}>
            Paquete pensado para la junta directiva: radio visual, voz institucional en antena, contenido editorial en
            Digest y presencia digital continua.
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={2.5}>
        {rows.map(({ servicio, detalle, beneficio, Icon, precioUsd }) => (
          <Grid item xs={12} sm={6} key={servicio}>
            <Box
              sx={{
                height: '100%',
                borderRadius: 3,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                border: `1px solid rgba(196, 164, 93, 0.35)`,
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(27,51,38,0.5)' : '#fff',
                boxShadow: theme.palette.mode === 'dark' ? '0 8px 32px rgba(0,0,0,0.3)' : '0 8px 30px rgba(27,51,38,0.07)',
                transition: 'transform 0.2s ease',
                '&:hover': { transform: 'translateY(-3px)' },
              }}
            >
              <Box
                sx={{
                  px: 2.5,
                  pt: 2.5,
                  pb: 2,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 2,
                  background:
                    theme.palette.mode === 'dark'
                      ? `linear-gradient(120deg, rgba(196,164,93,0.12) 0%, transparent 55%)`
                      : `linear-gradient(120deg, rgba(27,51,38,0.06) 0%, transparent 50%)`,
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: deep,
                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(196,164,93,0.25)',
                  }}
                >
                  <Icon sx={{ fontSize: 26 }} />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      gap: 1,
                      mb: 0.5,
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: h, lineHeight: 1.3, pr: 1 }}>
                      {servicio}
                    </Typography>
                    <Box
                      sx={{
                        flexShrink: 0,
                        px: 1.25,
                        py: 0.65,
                        borderRadius: 2,
                        border: `1px solid rgba(196, 164, 93, 0.55)`,
                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(196,164,93,0.12)' : 'rgba(196,164,93,0.16)',
                        textAlign: 'right',
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: gold, lineHeight: 1.2 }}>
                        ${precioUsd}
                      </Typography>
                      <Typography variant="caption" color="textSecondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                        USD / mes
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    component="ul"
                    sx={{
                      m: 0,
                      mt: 1,
                      pl: 2.25,
                      listStyleType: 'disc',
                      color: 'text.secondary',
                      '& li': { mb: 0.75, lineHeight: 1.65, pl: 0.35 },
                      '& li:last-of-type': { mb: 0 },
                    }}
                  >
                    {detalle.map((item) => (
                      <Typography key={item} component="li" variant="body2" color="textSecondary">
                        {item}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              </Box>
              <Box
                sx={{
                  mt: 'auto',
                  px: 2.5,
                  py: 2,
                  borderTop: `1px solid rgba(196, 164, 93, 0.25)`,
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(196,164,93,0.06)' : 'rgba(196,164,93,0.08)',
                }}
              >
                <Typography variant="caption" sx={{ color: gold, fontWeight: 700, letterSpacing: '0.08em' }}>
                  Beneficio
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: h, mt: 0.5, lineHeight: 1.55 }}>
                  {beneficio}
                </Typography>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>

      <Box
        sx={{
          mt: 4,
          p: { xs: 3, sm: 4 },
          borderRadius: 3,
          position: 'relative',
          overflow: 'hidden',
          background: `linear-gradient(115deg, ${deep} 0%, ${theme.palette.primary.dark} 45%, #0f1a14 100%)`,
          border: `1px solid rgba(196, 164, 93, 0.45)`,
          boxShadow: '0 20px 50px rgba(0,0,0,0.2)',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: -30,
            right: '8%',
            width: 140,
            height: 140,
            borderRadius: '50%',
            background: `radial-gradient(circle, rgba(196,164,93,0.25) 0%, transparent 70%)`,
            pointerEvents: 'none',
          }}
        />
        <Grid container spacing={3} alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid item xs={12} md={7}>
            <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700, fontFamily: 'Georgia, serif' }}>
              Total mensual estimado
            </Typography>
            <Typography
              variant="h3"
              sx={{
                color: gold,
                fontWeight: 800,
                mt: 1,
                letterSpacing: '-0.02em',
                textShadow: '0 2px 24px rgba(0,0,0,0.35)',
              }}
            >
              {`$${TOTAL_USD}`}{' '}
              <Box component="span" sx={{ fontSize: '1.1rem', fontWeight: 600, opacity: 0.95 }}>
                USD · cash
              </Box>
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.78)', mt: 1.25, lineHeight: 1.6 }}>
              {rows.map((r) => `$${r.precioUsd}`).join(' + ')} = <strong style={{ color: gold }}>${TOTAL_USD} USD</strong>{' '}
              al mes (plan institucional).
            </Typography>
          </Grid>
          <Grid item xs={12} md={5}>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.88)', lineHeight: 1.75 }}>
              Contratación mínima sugerida de <strong style={{ color: gold }}>3 meses</strong> para consolidar autoridad
              institucional, presencia en Digest y hábito de escucha en Radio Visual.
            </Typography>
          </Grid>
        </Grid>
      </Box>
      <PriceVatDisclaimer />
    </Box>
  );
};

export default PlanInstitutional;
